import { gql } from "~/__generated__"
import { BookmarkIndexItemFragment } from "~/__generated__/graphql"
import { useSafeMutation } from "~/common/use-safe-mutation"
import eyeOff from "~/images/eye-off"
import { toast } from "~/ui/use-toast"
import { cn } from "~/common/cn"

const BOOKMARK_HIDE_MUTATION = gql(/* GraphQL */ `
  mutation BookmarkHideButtonMutation($input: BookmarkHideInput!) {
    bookmarkHide(input: $input) {
      bookmark {
        id
        isHidden
      }
    }
  }
`)

const BOOKMARK_UNHIDE_MUTATION = gql(/* GraphQL */ `
  mutation BookmarkUnhideButtonMutation($input: BookmarkUnhideInput!) {
    bookmarkUnhide(input: $input) {
      bookmark {
        id
        isHidden
      }
    }
  }
`)

type BookmarkHideButtonProps = {
  bookmark: BookmarkIndexItemFragment
}

export const BookmarkHideButton = ({ bookmark }: BookmarkHideButtonProps) => {
  const [hideBookmark] = useSafeMutation(BOOKMARK_HIDE_MUTATION, {
    refetchQueries: ["GroupsFilters", "Bookmarks", "GroupDetail"],
    onError: (error) => {
      toast({
        title: "Error hiding bookmark",
        description: error.message,
        variant: "destructive",
      })
    },
  })

  const [unhideBookmark] = useSafeMutation(BOOKMARK_UNHIDE_MUTATION, {
    refetchQueries: ["GroupsFilters", "Bookmarks", "GroupDetail"],
    onError: (error) => {
      toast({
        title: "Error unhiding bookmark",
        description: error.message,
        variant: "destructive",
      })
    },
  })

  const handleToggleHide = async () => {
    try {
      if (bookmark.isHidden) {
        await unhideBookmark({
          variables: {
            input: {
              bookmarkId: bookmark.id,
            },
          },
        })
        toast({
          title: "Bookmark unhidden",
        })
      } else {
        await hideBookmark({
          variables: {
            input: {
              bookmarkId: bookmark.id,
            },
          },
        })
        toast({
          title: "Bookmark hidden",
        })
      }
      // refetchQueries will handle refreshing the data
    } catch (error) {
      console.error("Error toggling bookmark hide status:", error)
    }
  }

  return (
    <button
      onClick={(e) => {
        e.preventDefault()
        e.stopPropagation()
        handleToggleHide()
      }}
      className={cn("inline-flex h-6 w-6 items-center justify-center hover:bg-gray-400", {
        "bg-gray-200": bookmark.isHidden,
      })}
      title={bookmark.isHidden ? "Unhide bookmark" : "Hide bookmark"}
    >
      <img {...eyeOff} alt="Hide" />
    </button>
  )
}
